import { app } from "../config/firebaseConfig";
import { getFunctions, httpsCallable } from "firebase/functions";

// Espelha o padrão de cache em 2 camadas de videoApi.js (Map em memória +
// sessionStorage, TTL de 5 minutos): evita rechamar a Cloud Function toda vez
// que o usuário revisita um clipe já sintetizado nesta sessão do navegador.
// A function em si já faz cache-aside no Storage (mp3 + visemes.json), então
// este cache local é só uma segunda camada para não pagar nem a latência da
// chamada de rede quando o mesmo clipe é falado de novo na mesma sessão.
const CACHE_TTL_MS = 5 * 60 * 1000;
const inMemoryCache = new Map();

const buildCacheKey = (path, key) => `${path}::${key}`;

const readCache = (cacheKey) => {
  const now = Date.now();
  const mem = inMemoryCache.get(cacheKey);
  if (mem && mem.expiresAt > now) return mem.value;
  if (mem) inMemoryCache.delete(cacheKey);

  try {
    const raw = sessionStorage.getItem(`eia_avatar_speech:${cacheKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.expiresAt || parsed.expiresAt <= now) {
      sessionStorage.removeItem(`eia_avatar_speech:${cacheKey}`);
      return null;
    }
    inMemoryCache.set(cacheKey, parsed);
    return parsed.value || null;
  } catch {
    return null;
  }
};

const writeCache = (cacheKey, value) => {
  const payload = {
    value,
    expiresAt: Date.now() + CACHE_TTL_MS,
  };
  inMemoryCache.set(cacheKey, payload);
  try {
    sessionStorage.setItem(`eia_avatar_speech:${cacheKey}`, JSON.stringify(payload));
  } catch {
    // ignora limite de storage
  }
};

/**
 * Recupera (ou sintetiza) a fala do avatar barato do EIA para um clipe: áudio
 * (Azure Neural TTS) + eventos de viseme (fonema + tempo em ms), usados pelo
 * AvatarVisemePlayer para trocar o formato da boca em sincronia com o áudio.
 *
 * Cache-aside unificado: a Cloud Function `getOrSynthesizeAvatarSpeech` já
 * checa o Storage antes de chamar a Azure — este util só adiciona uma camada
 * de cache local (Map + sessionStorage) para não repetir nem a chamada de
 * rede dentro da mesma sessão do navegador.
 *
 * @param {Object} opts
 * @param {string} opts.interviewType
 * @param {string} [opts.company]
 * @param {string} opts.avatarId
 * @param {string|null} [opts.key] - chave estável do clipe (perguntas fixas
 *   têm key; perguntas dinâmicas geradas na hora não têm e pulam o cache).
 * @param {string} opts.text - texto a ser falado.
 * @param {string} opts.voiceId - nome da voz Azure (ex: "pt-BR-FranciscaNeural").
 * @returns {Promise<{audioSrc: string, visemes: Array<{visemeId:number, offsetMs:number}>}>}
 */
export async function getAvatarSpeech({ interviewType, company, avatarId, key, text, voiceId }) {
  const path = `eia/speech/${interviewType}/${company || 'default'}/${avatarId}`;
  const normalizedKey = typeof key === 'string' ? key.trim() : '';
  const cacheKey = normalizedKey ? buildCacheKey(path, normalizedKey) : null;

  if (cacheKey) {
    const cached = readCache(cacheKey);
    if (cached) return cached;
  }

  const functions = getFunctions(app, "southamerica-east1");
  const callable = httpsCallable(functions, "getOrSynthesizeAvatarSpeech");
  const { data } = await callable({
    path,
    key: normalizedKey || null,
    text,
    voiceId,
  });

  const audioSrc = data?.audioUrl
    ? data.audioUrl
    : `data:${data?.mimeType || 'audio/mpeg'};base64,${data?.audioBase64 || ''}`;

  const result = {
    audioSrc,
    visemes: Array.isArray(data?.visemes) ? data.visemes : [],
  };

  if (cacheKey) {
    writeCache(cacheKey, result);
  }

  return result;
}
